import { Box, Button, Flex, Tag, TagLabel, Text, VStack, Wrap, WrapItem } from '@chakra-ui/react';
import type { AftermarketCertification, PartType } from '../../../../hooks/useGetSettings';
import {
    AFTERMARKET_CERT_OPTIONS,
    MARKETPLACE_OPTIONS,
    PART_TYPE_OPTIONS,
    type PriceMatchDraftEntry,
    type PriceMatchSource,
} from '../../../../components/settings/priceMatchCriteria';
import VendorSearchField from '../vendors/VendorSearchField';

export interface PriceMatchCriteriaFieldsProps {
    value: PriceMatchDraftEntry;
    onChange: (next: PriceMatchDraftEntry) => void;
    /** Tighter labels and spacing, for the editor inside a card. */
    isCompact?: boolean;
    autoFocus?: boolean;
}

interface FieldProps {
    label: string;
    description?: string;
    isCompact: boolean;
    children: React.ReactNode;
}

const Field = ({ label, description, isCompact, children }: FieldProps) => (
    <Box>
        <Text
            fontSize={isCompact ? 'xs' : 'sm'}
            fontWeight={isCompact ? 'semibold' : 'medium'}
            color={isCompact ? 'gray.600' : 'gray.800'}
            mb={description ? 0.5 : 1.5}
        >
            {label}
        </Text>
        {description && (
            <Text fontSize="sm" color="gray.500" mb={2}>
                {description}
            </Text>
        )}
        {children}
    </Box>
);

/**
 * The three criteria a price-match card is built from — vendor, part type and
 * marketplace — in the order they are asked for. Shared by the creation pane on
 * the left of the order screen and by a card being edited in place on the
 * right, so both offer exactly the same choices.
 *
 * Certifications only appear under Aftermarket: they are the one part type that
 * has them, and an entry stores them only alongside that type.
 */
const PriceMatchCriteriaFields = ({
    value,
    onChange,
    isCompact = false,
    autoFocus = false,
}: PriceMatchCriteriaFieldsProps) => {
    const tagSize = isCompact ? 'sm' : 'md';

    const handleSelectPartType = (partType: PartType) => {
        const isSame = value.partType === partType;
        onChange({
            ...value,
            partType: isSame ? undefined : partType,
            // Certifications belong to Aftermarket, so they go with it.
            aftermarketCertifications: isSame || partType !== 'a/m' ? undefined : value.aftermarketCertifications,
        });
    };

    const handleToggleCert = (cert: AftermarketCertification) => {
        const current = value.aftermarketCertifications ?? [];
        onChange({
            ...value,
            aftermarketCertifications: current.includes(cert)
                ? current.filter((candidate) => candidate !== cert)
                : [...current, cert],
        });
    };

    const handleToggleSource = (source: PriceMatchSource) => {
        onChange({
            ...value,
            source: value.source.includes(source)
                ? value.source.filter((candidate) => candidate !== source)
                : [...value.source, source],
        });
    };

    return (
        <VStack align="stretch" spacing={isCompact ? 3 : 5}>
            <Field
                label="Vendor name"
                description={isCompact ? undefined : 'The vendor whose parts this card matches to.'}
                isCompact={isCompact}
            >
                {value.supplierId ? (
                    <Flex align="center" justify="space-between" gap={3}>
                        <Tag size={tagSize} borderRadius="full" colorScheme="partbay-blue" variant="subtle">
                            <TagLabel>{value.supplierName}</TagLabel>
                        </Tag>
                        <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="gray"
                            onClick={() =>
                                onChange({ ...value, supplierId: undefined, supplierName: undefined })
                            }
                        >
                            Change
                        </Button>
                    </Flex>
                ) : (
                    <VendorSearchField
                        autoFocus={autoFocus}
                        placeholder="Any vendor — search to narrow it"
                        onSelect={(supplier) =>
                            onChange({ ...value, supplierId: supplier._id, supplierName: supplier.title })
                        }
                    />
                )}
            </Field>

            <Field
                label="Part type"
                description={isCompact ? undefined : 'The type of part this card matches to.'}
                isCompact={isCompact}
            >
                <Wrap spacing={2}>
                    {PART_TYPE_OPTIONS.map((option) => {
                        const isSelected = value.partType === option.value;
                        return (
                            <WrapItem key={option.value}>
                                <Tag
                                    size={tagSize}
                                    variant={isSelected ? 'solid' : 'subtle'}
                                    colorScheme={isSelected ? 'green' : 'gray'}
                                    cursor="pointer"
                                    onClick={() => handleSelectPartType(option.value)}
                                >
                                    {option.label}
                                </Tag>
                            </WrapItem>
                        );
                    })}
                </Wrap>
            </Field>

            {value.partType === 'a/m' && (
                <Field
                    label="Certification"
                    description={
                        isCompact ? undefined : 'Limit the match to aftermarket parts with these certifications.'
                    }
                    isCompact={isCompact}
                >
                    <Wrap spacing={2}>
                        {AFTERMARKET_CERT_OPTIONS.map((option) => {
                            const isSelected = (value.aftermarketCertifications ?? []).includes(
                                option.value,
                            );
                            return (
                                <WrapItem key={option.value}>
                                    <Tag
                                        size={tagSize}
                                        variant={isSelected ? 'solid' : 'subtle'}
                                        colorScheme={isSelected ? 'purple' : 'gray'}
                                        cursor="pointer"
                                        onClick={() => handleToggleCert(option.value)}
                                    >
                                        {option.label}
                                    </Tag>
                                </WrapItem>
                            );
                        })}
                    </Wrap>
                </Field>
            )}

            <Field
                label="Marketplace"
                description={isCompact ? undefined : 'Where the prices this card matches to come from.'}
                isCompact={isCompact}
            >
                <Wrap spacing={2}>
                    {MARKETPLACE_OPTIONS.map((option) => {
                        const isSelected = value.source.includes(option.value);
                        return (
                            <WrapItem key={option.value}>
                                <Tag
                                    size={tagSize}
                                    variant={isSelected ? 'solid' : 'subtle'}
                                    colorScheme={isSelected ? 'orange' : 'gray'}
                                    cursor="pointer"
                                    onClick={() => handleToggleSource(option.value)}
                                >
                                    {option.label}
                                </Tag>
                            </WrapItem>
                        );
                    })}
                </Wrap>
            </Field>
        </VStack>
    );
};

export default PriceMatchCriteriaFields;
